import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Plus as PlusIcon } from 'react-feather';

import {
  Button,
  Checkbox,
  InputAdornment,
  InputHelperText,
  InputLabel,
  SelectField,
  Spinner,
  TextField,
  Tooltip,
  Typography,
} from 'ui-kit';
import { calculateYouMake, formatCurrency, getDigitsFromValue } from 'utils';
import { useStore } from 'contexts/store';
import { useOwnerDetails } from 'api/owners';
import { getFieldError } from '../utils';
import { useAuth } from 'contexts/auth/auth';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import OwnerProfitTooltip from 'ui-kit/OwnerProfitTooltip';
import { EPriceType, priceTimeLabelOptions } from './constants';
import { Col, Divider, Form, Row } from 'antd';
import _ from 'lodash';
import { validateProductPrices } from './utils';
import Stack from 'ui-kit/Stack';

interface Props {
  baseClass: string;
}

const PriceField = ({ baseClass }: Props) => {
  const { control, watch, setValue, setError } = useFormContext();
  const price = watch('product.price');
  const priceType = watch('product.priceType');
  const productPrices = watch('product.productPrices');
  const isEarlyCheckin = watch('product.isEarlyCheckin');
  const isLateCheckout = watch('product.isLateCheckout');

  const { id: ownerId } = useAuth();
  const { data, isLoading } = useOwnerDetails(ownerId);
  const { currency, commissionCutoff, commissionLow, commissionHigh } = useStore();
  const hasPremiumPlan = useMemo(() => data?.plan !== 'BASIC', [data?.plan]);

  const {
    fields,
    append: appendPrice,
    remove: removePrice,
  } = useFieldArray({
    control,
    name: 'product.productPrices',
  });

  const isSpecialProduct = useMemo(
    () => isEarlyCheckin || isLateCheckout,
    [isEarlyCheckin, isLateCheckout],
  );

  const handleChangePriceType = useCallback(
    (value) => {
      setValue('product.priceType', value, { shouldValidate: true, shouldDirty: true });
      setValue('product.price', '0.00', { shouldValidate: true, shouldDirty: true });

      if (value === EPriceType.MULTIPLE) {
        // If the price type is changed to MULTIPLE from STATIC we want to set the price as the first price
        // If the price type is changed to MULTIPLE from VARIABLE, the price will be 0.00
        setValue('product.productPrices', [{ label: null, price, timeLabel: null }], {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        setValue('product.productPrices', [], { shouldValidate: true, shouldDirty: true });
      }
    },
    [setValue, price],
  );

  const handleChangeVariablePrice = useCallback(
    (e) => {
      const newValue = e.target.checked;
      handleChangePriceType(newValue ? EPriceType.VARIABLE : EPriceType.STATIC);
    },
    [handleChangePriceType],
  );

  const handleAddPriceVariation = useCallback(() => {
    // If the price type is STATIC or VARIABLE, we want to set it to MULTIPLE
    if (priceType !== EPriceType.MULTIPLE) {
      handleChangePriceType(EPriceType.MULTIPLE);
    }

    appendPrice({ label: null, price: '', timeLabel: null });
  }, [appendPrice, handleChangePriceType, priceType]);

  const handleRemovePriceVariation = useCallback(
    ({ index }) => {
      /**
       * When the product is special (early checkin or late checkout) we want always to keep at least one option
       *
       * When the user removes the before last price variation, we want to set the last price as the price
       * and set the price type to STATIC
       *  */
      if (productPrices?.length === 2 && !isSpecialProduct) {
        const lastElement = productPrices[index === 0 ? 1 : 0];

        setValue('product.productPrices', [], { shouldValidate: true, shouldDirty: true });
        setValue('product.priceType', EPriceType.STATIC, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue('product.price', lastElement.price, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        removePrice(index);
      }
    },
    [productPrices, removePrice, isSpecialProduct],
  );

  useEffect(() => {
    validateProductPrices({
      productPrices,
      priceType,
      setErrorCallback: setError,
      isEarlyCheckin,
      isLateCheckout,
    });
  }, []);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div>
      {priceType === EPriceType.MULTIPLE ? (
        <div className={`${baseClass}-price-variations`}>
          {fields.map((field, index) => (
            <Row gutter={[16, 16]} key={field.id} className="price-variation">
              <Col xs={6}>
                {isEarlyCheckin || isLateCheckout ? (
                  <Controller
                    control={control}
                    name={`product.productPrices.${index}.timeLabel`}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                      return (
                        <Form.Item style={{ flex: 1 }}>
                          <SelectField
                            fullWidth
                            className="timeLabel"
                            options={priceTimeLabelOptions}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            error={!!error}
                            errorMessage={error?.message}
                          />
                        </Form.Item>
                      );
                    }}
                  />
                ) : (
                  <Controller
                    control={control}
                    name={`product.productPrices.${index}.label`}
                    render={({
                      field: { ref, value, onChange, onBlur },
                      fieldState: { error },
                    }) => {
                      return (
                        <Form.Item style={{ flex: 1 }}>
                          <TextField
                            ref={ref}
                            required
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            error={!!error}
                            errorMessage={error?.message}
                          />
                        </Form.Item>
                      );
                    }}
                  />
                )}
              </Col>
              <Col xs={6}>
                <Controller
                  control={control}
                  name={`product.productPrices.${index}.price`}
                  render={({ field: { ref, value, onChange, onBlur }, fieldState: { error } }) => (
                    <Form.Item>
                      <TextField
                        ref={ref}
                        placeholder="0.00"
                        required
                        InputProps={{
                          startAdornment: <InputAdornment>$</InputAdornment>,
                        }}
                        value={value}
                        onChange={(e) => onChange(getDigitsFromValue(e.target.value))}
                        onBlur={onBlur}
                        error={!!error}
                        errorMessage={error?.message}
                      />
                    </Form.Item>
                  )}
                />
              </Col>

              <Col xs={4} style={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleRemovePriceVariation({ index })}
                  disabled={isSpecialProduct && productPrices.length === 1}
                >
                  <DeleteOutlined />
                </Button>
              </Col>

              {!hasPremiumPlan && (
                <Col xs={8}>
                  <Controller
                    control={control}
                    name={`product.productPrices.${index}.price`}
                    render={({ field: { value } }) => {
                      const youMake = calculateYouMake({
                        price: value,
                        commissionLow,
                        commissionHigh,
                        commissionCutoff,
                        hasPremiumPlan,
                      });

                      if (value) {
                        return (
                          <div className={`price-variation-price-content`}>
                            <Typography variant="caption">
                              Your estimated profit <OwnerProfitTooltip />
                              <Typography
                                variant="body1"
                                color="secondary"
                                className={`price-variation-price-content-value`}
                              >
                                {formatCurrency(youMake, currency)}
                              </Typography>
                            </Typography>
                          </div>
                        );
                      }

                      return null;
                    }}
                  />
                </Col>
              )}
            </Row>
          ))}
          <Controller
            control={control}
            name="product.productPrices"
            render={({ fieldState: { error } }) => (
              <>
                {error && error.message && (
                  <InputHelperText type="error" showHelperIcon={true}>
                    {error.message}
                  </InputHelperText>
                )}
              </>
            )}
          />
        </div>
      ) : (
        <Controller
          control={control}
          name="product.price"
          render={({ field: { value, ref, onChange, ...rest }, fieldState }) => {
            const youMake = calculateYouMake({
              price: value,
              commissionLow,
              commissionHigh,
              commissionCutoff,
              hasPremiumPlan,
            });
            return (
              <div className={`${baseClass}-price`}>
                <TextField
                  label="Product price"
                  placeholder="0.00"
                  required={priceType === EPriceType.STATIC}
                  InputProps={{
                    startAdornment: <InputAdornment>$</InputAdornment>,
                  }}
                  inputRef={ref}
                  value={value ?? ''}
                  onChange={(e) => onChange(getDigitsFromValue(e.target.value))}
                  {...getFieldError(fieldState)}
                  {...rest}
                  disabled={priceType === EPriceType.VARIABLE}
                />
                {!hasPremiumPlan && (
                  <div className={`${baseClass}-price-content`}>
                    <div>
                      <InputLabel label="Your estimated profit" /> <OwnerProfitTooltip />
                    </div>
                    <Typography
                      variant="h6"
                      color="secondary"
                      className={`${baseClass}-price-content-value`}
                    >
                      {priceType === EPriceType.VARIABLE ? '-' : formatCurrency(youMake, currency)}
                    </Typography>
                  </div>
                )}
              </div>
            );
          }}
        />
      )}
      <Stack justifyContent="space-between" alignItems="center" style={{ marginTop: '16px' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PlusIcon />}
          onClick={handleAddPriceVariation}
        >
          Add multiple prices
        </Button>
        {!isSpecialProduct && (
          <Checkbox
            checked={priceType === EPriceType.VARIABLE}
            onChange={handleChangeVariablePrice}
          >
            Variable price{' '}
            <Tooltip title="Guest sets price, or ask your guest to coordinate price ahead of time (via your product description below).">
              <ExclamationCircleOutlined />
            </Tooltip>
          </Checkbox>
        )}
      </Stack>
      <Divider style={{ marginBottom: 0 }} />
    </div>
  );
};

export default PriceField;
